import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Stamp, Camera, PackageOpen, Loader2, Truck, MessageCircle, Paperclip, X } from "lucide-react";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const ICONS = { "logo-chocolates": Stamp, "photo-chocolates": Camera, "branded-packaging": PackageOpen };
const EMPTY = { name: "", email: "", phone: "", company: "", service: "", quantity: "", flavour: "", design_details: "", packaging: "", delivery_city: "", message: "" };
const inp = "bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7]";

const ServiceCard = ({ s, i, onEnquire }) => {
  const Icon = ICONS[s.service_id] || Stamp;
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ delay: i * 0.12 }} className="lux-card p-7 flex flex-col hover-lift" data-testid={`custom-service-${s.service_id}`}>
      {s.image ? <img src={s.image} alt={s.name} className="h-44 w-full object-cover rounded-xl mb-5" />
        : <div className="h-14 w-14 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] flex items-center justify-center mb-5"><Icon size={26} /></div>}
      <span className="eyebrow">{s.category}</span>
      <h3 className="font-display text-2xl mt-2">{s.name}</h3>
      <p className="text-[#C8B8AB] text-sm mt-3">{s.description}</p>
      <dl className="mt-5 space-y-3 text-sm flex-1">
        <div><dt className="text-[#8C7B6E] text-xs uppercase tracking-widest">Options</dt><dd className="text-[#FDFBF7] mt-1">{s.options}</dd></div>
        <div><dt className="text-[#8C7B6E] text-xs uppercase tracking-widest">Ideal for</dt><dd className="text-[#FDFBF7] mt-1">{s.suitable_for}</dd></div>
        <p className="text-[#8C7B6E] text-xs italic">{s.info}</p>
      </dl>
      <div className="mt-6 pt-5 border-t border-[#3B251D] flex items-center justify-between gap-3">
        <div>
          <p data-testid={`custom-price-${s.service_id}`} className="font-display text-lg gold-gradient-text">{s.pricing}</p>
          <p className="text-[11px] text-[#8C7B6E]">{s.availability} · Bulk orders</p>
        </div>
        <Button data-testid={`custom-enquire-${s.service_id}`} onClick={() => onEnquire(s.name)} className="btn-gold rounded-full px-5">Enquire</Button>
      </div>
      <p className="text-[11px] text-[#8C7B6E] mt-3">{s.note}</p>
    </motion.div>
  );
};

const ACCEPT = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

const Attachments = ({ files, setFiles }) => {
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length + picked.length > 3) return toast.error("You can attach up to 3 files");
    for (const f of picked) {
      if (!ACCEPT.includes(f.type)) return toast.error(`${f.name}: only JPG, PNG, WEBP or PDF`);
      if (f.size > 10 * 1024 * 1024) return toast.error(`${f.name} is larger than 10 MB`);
    }
    setBusy(true);
    try {
      for (const f of picked) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/enquiry-uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, data]);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <div className="sm:col-span-2" data-testid="custom-attachments">
      <label className={`flex items-center justify-center gap-2 border border-dashed border-[#3B251D] hover:border-[#D4AF37] rounded-xl py-5 text-sm text-[#C8B8AB] cursor-pointer transition-colors ${files.length >= 3 ? "opacity-50 pointer-events-none" : ""}`}>
        {busy ? <Loader2 className="animate-spin text-[#D4AF37]" size={18} /> : <Paperclip size={18} className="text-[#D4AF37]" />}
        {busy ? "Uploading…" : "Attach your logo or photo (JPG, PNG, WEBP, PDF · up to 3 files, 10 MB each)"}
        <input type="file" multiple accept={ACCEPT.join(",")} onChange={onPick} className="hidden" data-testid="custom-file-input" />
      </label>
      {files.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {files.map((f) => (
            <li key={f.file_id} data-testid={`custom-file-${f.file_id}`} className="flex items-center gap-2 bg-[#241611] border border-[#3B251D] rounded-full pl-3 pr-1 py-1 text-xs text-[#FDFBF7]">
              <span className="max-w-[160px] truncate">{f.name}</span>
              <button type="button" onClick={() => setFiles(files.filter((x) => x.file_id !== f.file_id))} className="h-6 w-6 rounded-full flex items-center justify-center text-[#8C7B6E] hover:text-[#DC2626]" aria-label="Remove file"><X size={13} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Field = ({ k, form, set, placeholder, span }) => (
  <Input data-testid={`custom-${k.replace("_", "-")}`} placeholder={placeholder} value={form[k]}
    onChange={(e) => set({ ...form, [k]: e.target.value })} className={`${inp} ${span ? "sm:col-span-2" : ""}`} />
);

export default function Customisation() {
  const { config } = useStore();
  const [services, setServices] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);

  useEffect(() => { api.get("/custom-services").then((r) => setServices(r.data)); }, []);

  const enquire = (name) => {
    setForm((f) => ({ ...f, service: name }));
    document.getElementById("custom-enquiry")?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async () => {
    if (!form.name || !form.email || !form.phone || !form.service) return toast.error("Please fill name, email, phone and service");
    setLoading(true);
    try {
      await api.post("/gift-requests", { type: "customisation", ...form, attachments: files.map((f) => f.file_id) });
      toast.success("Enquiry received! Our team will share a personalised quote shortly.");
      setForm(EMPTY);
      setFiles([]);
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || "Could not submit. Please check your details.");
    } finally { setLoading(false); }
  };

  const wa = `https://wa.me/${config.whatsapp_number || "917622927835"}?text=${encodeURIComponent(`Hello SCHOKODALE, I'd like a bulk quote for ${form.service || "customised chocolates"}.`)}`;

  return (
    <div data-testid="customisation-page">
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(212,175,55,0.18),transparent_60%)]" />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8">
          <span className="eyebrow">Customisation Menu</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl">Chocolates that carry your name</h1>
          <p className="text-[#C8B8AB] mt-5 max-w-2xl">Logo chocolates, photo chocolates and bespoke branded packaging — crafted to order for your business, celebration or event. Every quote is personalised.</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 pb-16 grid md:grid-cols-3 gap-6">
        {services.map((s, i) => <ServiceCard key={s.service_id} s={s} i={i} onEnquire={enquire} />)}
      </section>

      <section id="custom-enquiry" className="max-w-4xl mx-auto px-4 sm:px-8 pb-24">
        <div className="lux-card p-8">
          <h2 className="font-display text-2xl">Request a Bulk Quote</h2>
          <p className="text-[#8C7B6E] text-sm mt-2 flex items-center gap-2"><Truck size={15} className="text-[#D4AF37]" /> Pan-India delivery available · Complimentary delivery on orders ₹{(config.free_delivery_threshold || 3500).toLocaleString("en-IN")} & above</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <Field k="name" form={form} set={setForm} placeholder="Full Name *" />
            <Field k="phone" form={form} set={setForm} placeholder="Phone *" />
            <Field k="email" form={form} set={setForm} placeholder="Email *" />
            <Field k="company" form={form} set={setForm} placeholder="Company / Event (optional)" />
            <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
              <SelectTrigger data-testid="custom-service-select" className={inp}><SelectValue placeholder="Service required *" /></SelectTrigger>
              <SelectContent>{services.map((s) => <SelectItem key={s.service_id} value={s.name} data-testid={`custom-service-option-${s.service_id}`}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            <Field k="quantity" form={form} set={setForm} placeholder="Quantity (pieces)" />
            <Field k="flavour" form={form} set={setForm} placeholder="Chocolate type / flavour" />
            <Field k="delivery_city" form={form} set={setForm} placeholder="Delivery city" />
            <Field k="design_details" form={form} set={setForm} placeholder="Logo / photo / message details" span />
            <Field k="packaging" form={form} set={setForm} placeholder="Packaging preference (box, ribbon, branding)" span />
            <Attachments files={files} setFiles={setFiles} />
            <Textarea data-testid="custom-message" placeholder="Anything else we should know…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inp} sm:col-span-2`} />
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            <Button data-testid="custom-submit-btn" onClick={submit} disabled={loading} className="btn-gold rounded-full px-8 h-12">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Submit Enquiry"}
            </Button>
            <a data-testid="custom-whatsapp-btn" href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[#25D366]/60 text-[#25D366] px-6 h-12 hover:bg-[#25D366]/10 transition-colors">
              <MessageCircle size={18} /> Enquire on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
